import React, { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { HUMANS, scenarios } from '../data/populations';
import { useScenario } from '../contexts/ScenarioContext';
import { allParity, ParityOutcome, ratioAt, yearLabel } from '../utils/parity';
import { formatCompact, formatInt, formatRatio } from '../utils/format';
import { PageHeader } from '../components/PageHeader';
import { SourceRef } from '../components/SourceRef';
import { DataBadge } from '../components/DataBadge';
import { COMPILED_LABEL } from '../data/sources';
function useNow(stepMs: number) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), stepMs);
    return () => window.clearInterval(id);
  }, [stepMs]);
  return now;
}
function Countdown({
  outcome
}: {
  outcome: ParityOutcome;
}) {
  const reduce = useReducedMotion();
  const now = useNow(reduce ? 1000 : 250);
  if (outcome.kind === 'never') return <span className="text-faint" data-id="element-1400" data-mp="3qa71dy3qx8n4">never, at current rates</span>;
  if (outcome.kind === 'crossed') return <span className="text-human" data-id="element-1401" data-mp="3lbdpga43xpp2">crossed before our first dated snapshot</span>;
  const diff = Math.abs(outcome.at - now);
  const d = Math.floor(diff / 86_400_000);
  const h = Math.floor(diff % 86_400_000 / 3_600_000);
  const m = Math.floor(diff % 3_600_000 / 60_000);
  const s = Math.floor(diff % 60_000 / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return <span className="tabular" data-id="element-1402" data-mp="84lp4lpkwy00">
      {outcome.at <= now ? 'reached ' : ''}
      {formatInt(d)}d {pad(h)}h {pad(m)}m {pad(s)}s{outcome.at <= now ? ' ago' : ' to go'}
    </span>;
}
const W = 760;
const H = 260;
const PAD = {
  l: 48,
  r: 16,
  t: 16,
  b: 28
};
const START = HUMANS.baseTime;
const END = Date.UTC(2034, 0, 1);
const LMIN = -3;
const LMAX = 1;
function x(t: number) {
  return PAD.l + (t - START) / (END - START) * (W - PAD.l - PAD.r);
}
function y(r: number) {
  const l = Math.min(LMAX, Math.max(LMIN, Math.log10(r)));
  return PAD.t + (LMAX - l) / (LMAX - LMIN) * (H - PAD.t - PAD.b);
}
export function Parity() {
  const {
    scenarioId
  } = useScenario();
  const tone: Record<string, string> = {
    verifiable: 'rgb(var(--c-faint))',
    device: 'rgb(var(--c-machine))',
    ambient: 'rgb(var(--c-human))'
  };
  const years = [2027, 2028, 2029, 2030, 2031, 2032, 2033];
  return <>
      <PageHeader title="Parity" lede="When will machines outnumber people? Under each scenario we extend today’s counts at their stated rates and solve for the crossover. All the math is shown below." data-id="element-1403" data-mp="785ezvbowmrv">
        <DataBadge mode="snapshot" source="Scenario bases" asOf={COMPILED_LABEL} data-id="element-1404" data-mp="h6322fhtcdiw" />
      </PageHeader>

      <section className="mx-auto max-w-[1400px] px-5 md:px-8" data-id="element-1405" data-mp="l87vbd4cn850">
        <div className="mt-8 grid border-y border-line md:grid-cols-3 md:divide-x md:divide-line" data-id="element-1406" data-mp="2ixpmfy779a6e">
          {allParity.map(({
          scenario: s,
          central,
          high
        }) => {
          const active = s.id === scenarioId;
          return <article key={s.id} className={`py-6 md:px-6 md:first:pl-0 ${active ? '' : 'opacity-80'}`} data-id="element-1407" data-mp="2fg202j4xz659">
                <p className="flex items-center gap-2 text-[13px] text-muted" data-id="element-1408" data-mp="2ao0mmtw1f9dn">
                  {s.label}
                  {active && <span className="rounded-full border border-machine/50 px-1.5 py-px font-mono text-[10px] text-machine" data-id="element-1409" data-mp="25qia6titac0w">active</span>}
                </p>
                <p className="mt-2 font-serif text-3xl text-ink" data-id="element-1410" data-mp="2yf641dhmm9q2">
                  {central.kind === 'crossed' ? 'Already past parity' : central.kind === 'never' ? 'No crossover' : yearLabel(central)}
                  <SourceRef id="fermi" estimate={{
                low: yearLabel(high),
                high: yearLabel(central)
              }} note="Linear extrapolation of our assumed rates." data-id="element-1411" data-mp="2pdgaa109uyp4" />
                </p>
                <p className="mt-3 font-mono text-[15px] text-machine" data-id="element-1412" data-mp="1dyt13lvf509q">
                  <Countdown outcome={central} data-id="element-1413" data-mp="1e046tr8fci3h" />
                </p>
                <p className="mt-3 text-[12.5px] text-muted" data-id="element-1414" data-mp="18yohhmz7rpfa">
                  High bound (range-high count, 2× rate): <span className="font-mono text-ink" data-id="element-1415" data-mp="2wvcspxzzfr8i">{high.kind === 'crossed' ? 'already crossed' : yearLabel(high)}</span>
                </p>
              </article>;
        })}
        </div>

        <div className="mt-12 grid gap-12 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]" data-id="element-1416" data-mp="2nx0ub9svjp5i">
          <figure data-id="element-1417" data-mp="2rycolsmp0yk1">
            <figcaption className="text-[15px] font-medium text-ink" data-id="element-1418" data-mp="2rxuhg0yhszxc">
              Agents per human, projected · log scale
              <SourceRef id="fermi" data-id="element-1419" data-mp="3fl2pgqnb2n32" />
            </figcaption>
            <p className="mt-1 text-[12.5px] text-muted" data-id="element-1420" data-mp="3kkp4n5mp2dei">
              Lines start at our only dated snapshot (Oct 5, 2026) and extend at each scenario’s stated rate. As more quarterly snapshots are published they will be plotted as points.
            </p>
            <svg viewBox={`0 0 ${W} ${H}`} className="mt-4 w-full" role="img" aria-label="Projected agents-per-human ratio for three scenarios from 2026 to 2034, with parity line at 1.0" data-id="element-1421" data-mp="1v7brqs0inh1s">
              {[-3, -2, -1, 0, 1].map(l => <g key={l} data-id="element-1422" data-mp="1rntgjnjv90ra">
                  <line x1={PAD.l} x2={W - PAD.r} y1={y(10 ** l)} y2={y(10 ** l)} stroke="rgb(var(--c-line))" strokeDasharray={l === 0 ? undefined : '2 4'} data-id="element-1423" data-mp="1rr7k2z49ng0b" />
                  <text x={PAD.l - 8} y={y(10 ** l) + 4} textAnchor="end" className="fill-[rgb(var(--c-faint))] font-mono text-[10px]" data-id="element-1424" data-mp="21m997uyjki2w">
                    {l === 0 ? '1.0' : 10 ** l}
                  </text>
                </g>)}
              <text x={W - PAD.r} y={y(1) - 6} textAnchor="end" className="fill-[rgb(var(--c-human))] font-mono text-[10px]" data-id="element-1425" data-mp="18wk4p7mmhzwj">
                parity = 1.0
              </text>
              {years.map(yr => <text key={yr} x={x(Date.UTC(yr, 0, 1))} y={H - 8} textAnchor="middle" className="fill-[rgb(var(--c-faint))] font-mono text-[10px]" data-id="element-1426" data-mp="1dxq1bgjem7bc">
                  {yr}
                </text>)}
              {scenarios.map(s => {
              const pts = Array.from({
                length: 41
              }, (_, i) => START + (END - START) * i / 40).map(t => `${x(t).toFixed(1)},${y(ratioAt(s, t)).toFixed(1)}`);
              return <g key={s.id} data-id="element-1427" data-mp="2q9d5ljjt51ng">
                    <polyline points={pts.join(' ')} fill="none" stroke={tone[s.id]} strokeWidth={s.id === scenarioId ? 2.5 : 1.5} data-id="element-1428" data-mp="2q8aepbmhiz7j" />
                    <circle cx={x(START)} cy={y(ratioAt(s, START))} r={3.5} fill={tone[s.id]} data-id="element-1429" data-mp="2z2gow8h4w8q0" />
                  </g>;
            })}
            </svg>
            <ul className="mt-2 flex flex-wrap gap-4 text-[12px] text-muted" data-id="element-1430" data-mp="22eitn3h4m3i6">
              {scenarios.map(s => <li key={s.id} className="inline-flex items-center gap-1.5" data-id="element-1431" data-mp="2bd46qj3twqbk">
                  <span className="h-0.5 w-4" style={{
                background: tone[s.id]
              }} aria-hidden="true" data-id="element-1432" data-mp="2bf8lxx76hbr5" />
                  {s.label} · now {formatRatio(ratioAt(s, Date.now()))}
                </li>)}
            </ul>
          </figure>

          <section aria-labelledby="math-title" data-id="element-1433" data-mp="3rqumwo88351k">
            <h2 id="math-title" className="text-[15px] font-medium text-ink" data-id="element-1434" data-mp="3rpjcw6o6p120">
              The math
            </h2>
            <div className="mt-3 space-y-4 text-[13.5px] leading-relaxed text-ink/85" data-id="element-1435" data-mp="5rrcx211gj3t">
              <p data-id="element-1436" data-mp="swui16zgwk8">Both populations grow linearly from the same dated base:</p>
              <pre className="overflow-x-auto rounded-md border border-line bg-surface p-3 font-mono text-[12px] text-ink" data-id="element-1437" data-mp="3s3ydmxraga31">
{`humans(t)   = H₀ + b·t
machines(t) = M₀ + a·t
parity  ⇔   t = (H₀ − M₀) / (a − b)`}
              </pre>
              <p data-id="element-1438" data-mp="3j2gokpvwky27">
                H₀ = {formatInt(HUMANS.base)}, b = {HUMANS.ratePerSec}/s
                <SourceRef id="worldometer" data-id="element-1439" data-mp="3e7t4r4bugrty" />. Each scenario supplies M₀ and a
                <SourceRef id="fermi" data-id="element-1440" data-mp="3975foo76el2d" />:
              </p>
              <ul className="space-y-1.5 font-mono text-[12px]" data-id="element-1441" data-mp="355jw6k04z8j7">
                {scenarios.map(s => <li key={s.id} data-id="element-1442" data-mp="17fk2kkk15w1g">
                    {s.label}: M₀ = {formatCompact(s.count)}, a = {s.ratePerSec}/s
                    {s.ratePerSec <= HUMANS.ratePerSec ? ' → a ≤ b, no crossover' : s.count >= HUMANS.base ? ' → M₀ > H₀, already crossed' : ` → t = ${((HUMANS.base - s.count) / (s.ratePerSec - HUMANS.ratePerSec) / 31_557_600).toFixed(1)} yrs`}
                  </li>)}
              </ul>
              <p className="text-muted" data-id="element-1443" data-mp="1cvu6bx6x3r7t">
                Linear growth is a conservative assumption: device and agent counts have historically grown faster than linearly. The high bound uses each range’s top count and twice the rate, which is where the homepage’s “2026–2027” window for the device scenario comes from.
              </p>
            </div>
          </section>
        </div>
      </section>
    </>;
}